'use client';

import {
  Sparkles,
  Target,
  Mic,
  FileText,
  Linkedin,
  BarChart3,
  Kanban,
  Camera,
  Check,
  X,
  Minus,
} from 'lucide-react';
import { ScrollReveal } from '@/components/ScrollReveal';
import Link from 'next/link';

const features = [
  {
    icon: BarChart3,
    title: 'ATS Scoring & Keyword Analysis',
    desc: 'Get a detailed ATS match score before you apply. See exactly which keywords are missing and how to add them without keyword stuffing.',
  },
  {
    icon: Mic,
    title: '3D Avatar Mock Interviews',
    desc: 'Practice with AI interviewers that use 3D avatars and voice. Get scored on your responses with company-specific coaching for 40+ employers.',
  },
  {
    icon: FileText,
    title: 'AI Cover Letter Generator',
    desc: 'Generate a matching cover letter alongside every resume. Both documents are tailored to the same job description for a cohesive application.',
  },
  {
    icon: Linkedin,
    title: 'LinkedIn Optimization Suite',
    desc: 'Optimize your LinkedIn profile section by section. Improve your headline, summary, and experience to match the roles you are targeting.',
  },
  {
    icon: Kanban,
    title: 'Job Tracker Kanban Board',
    desc: 'Track every application from saved to offer in a visual Kanban board. Never lose track of where you stand with each company.',
  },
  {
    icon: Camera,
    title: 'AI Professional Headshots',
    desc: 'Generate professional headshots from your selfies for LinkedIn and applications. No photographer needed.',
  },
];

const competitors = [
  {
    name: 'Superpower Resume',
    ats: 'full',
    mockInterviews: 'full',
    coverLetter: 'full',
    linkedin: 'full',
    freePlan: '3 resumes/mo',
    pricing: '$19/mo',
    highlight: true,
  },
  {
    name: 'Resume Genius',
    ats: 'basic',
    mockInterviews: 'none',
    coverLetter: 'basic',
    linkedin: 'none',
    freePlan: 'Limited',
    pricing: '$8/mo',
    highlight: false,
  },
  {
    name: 'Zety',
    ats: 'basic',
    mockInterviews: 'none',
    coverLetter: 'full',
    linkedin: 'none',
    freePlan: 'None',
    pricing: '$24/mo',
    highlight: false,
  },
  {
    name: 'Teal',
    ats: 'paid',
    mockInterviews: 'paid',
    coverLetter: 'paid',
    linkedin: 'none',
    freePlan: 'Generous',
    pricing: '$29/mo',
    highlight: false,
  },
  {
    name: 'Kickresume',
    ats: 'basic',
    mockInterviews: 'none',
    coverLetter: 'full',
    linkedin: 'none',
    freePlan: 'Limited',
    pricing: '$19/mo',
    highlight: false,
  },
];

function FeatureIcon({ level }: { level: string }) {
  if (level === 'full')
    return <Check className="w-5 h-5 text-green-600 mx-auto" />;
  if (level === 'basic' || level === 'paid')
    return <Minus className="w-5 h-5 text-yellow-500 mx-auto" />;
  return <X className="w-5 h-5 text-gray-300 mx-auto" />;
}

function FeatureLabel({ level }: { level: string }) {
  if (level === 'full') return 'Yes';
  if (level === 'basic') return 'Basic';
  if (level === 'paid') return 'Paid only';
  return 'No';
}

export default function ATSResumeBuilderContent({
  faqItems,
}: {
  faqItems: { q: string; a: string }[];
}) {
  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-50 via-white to-stone-100 py-16 px-4 flex flex-col items-center">
      <main className="w-full max-w-5xl mx-auto">
        {/* Hero */}
        <ScrollReveal>
          <section className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              Beat ATS Filters
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-6">
              AI Resume Builder That<br className="hidden md:block" /> Beats ATS Filters
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-4">
              Build ATS-optimized resumes from your real career history. Get an ATS match score,
              practice with AI mock interviews, generate cover letters, and optimize your
              LinkedIn profile — all in one platform.
            </p>
            <p className="text-gray-500 max-w-xl mx-auto mb-8">
              Superpower Resume combines resume building, ATS scoring, interview prep, and
              LinkedIn optimization so you can stop juggling five different tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.superpowerresume.com"
                className="inline-block bg-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg text-lg hover:bg-blue-700 hover:scale-105 hover:shadow-xl transition-all duration-200"
              >
                Try Free — No Credit Card
              </a>
              <Link
                href="/features"
                className="inline-block border-2 border-blue-600 text-blue-700 font-bold py-3 px-8 rounded-xl text-lg hover:bg-blue-50 transition-all duration-200"
              >
                See All Features
              </Link>
            </div>
          </section>
        </ScrollReveal>

        {/* What Makes It Different */}
        <section className="mb-20">
          <ScrollReveal>
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              More Than a Resume Builder
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Most resume builders stop at formatting. Superpower Resume covers the full job
              search lifecycle — from resume to interview to offer.
            </p>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map(({ icon: Icon, title, desc }, i) => (
              <ScrollReveal key={title} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 h-full">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              AI Resume Builder Comparison
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              See how Superpower Resume stacks up against Resume Genius, Zety, Teal, and
              Kickresume across the features that matter most.
            </p>
            <div className="rounded-2xl shadow-lg bg-white border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-4 text-left font-bold text-gray-800">Platform</th>
                      <th className="px-4 py-4 text-center font-bold text-gray-800">
                        ATS Scoring
                      </th>
                      <th className="px-4 py-4 text-center font-bold text-gray-800">
                        Mock Interviews
                      </th>
                      <th className="px-4 py-4 text-center font-bold text-gray-800">
                        Cover Letter
                      </th>
                      <th className="px-4 py-4 text-center font-bold text-gray-800">
                        LinkedIn Optimization
                      </th>
                      <th className="px-4 py-4 text-center font-bold text-gray-800">
                        Free Plan
                      </th>
                      <th className="px-4 py-4 text-center font-bold text-gray-800">Pricing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((c, idx) => (
                      <tr
                        key={c.name}
                        className={`${c.highlight ? 'bg-blue-50/60' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                      >
                        <td
                          className={`px-4 py-4 font-medium ${c.highlight ? 'text-blue-700 font-bold' : 'text-gray-900'}`}
                        >
                          {c.name}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <FeatureIcon level={c.ats} />
                          <span className="block text-xs text-gray-500 mt-1">
                            <FeatureLabel level={c.ats} />
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <FeatureIcon level={c.mockInterviews} />
                          <span className="block text-xs text-gray-500 mt-1">
                            <FeatureLabel level={c.mockInterviews} />
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <FeatureIcon level={c.coverLetter} />
                          <span className="block text-xs text-gray-500 mt-1">
                            <FeatureLabel level={c.coverLetter} />
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <FeatureIcon level={c.linkedin} />
                          <span className="block text-xs text-gray-500 mt-1">
                            <FeatureLabel level={c.linkedin} />
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center text-gray-700">{c.freePlan}</td>
                        <td
                          className={`px-4 py-4 text-center font-medium ${c.highlight ? 'text-blue-700' : 'text-gray-700'}`}
                        >
                          {c.pricing}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* How ATS Works */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              How Applicant Tracking Systems Work
            </h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Over 97% of Fortune 500 companies use ATS software to filter resumes before a human
              ever sees them. Understanding how they work is the first step to beating them.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Keyword Matching</h3>
                <p className="text-gray-600">
                  ATS systems scan for specific keywords from the job description. If your resume
                  does not include them, it gets filtered out — even if you are qualified. An{' '}
                  <Link href="/ai-resume-builder" className="text-blue-600 hover:underline">
                    AI resume builder
                  </Link>{' '}
                  automatically matches keywords from the job posting.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Formatting Rules</h3>
                <p className="text-gray-600">
                  Tables, columns, headers/footers, and graphics break most ATS parsers. Your
                  resume needs clean, single-column formatting with standard section headers. Our{' '}
                  <Link href="/tools/ats-resume-checker" className="text-blue-600 hover:underline">
                    ATS resume checker
                  </Link>{' '}
                  validates formatting automatically.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Ranking & Scoring</h3>
                <p className="text-gray-600">
                  ATS software ranks candidates by match percentage. Resumes with higher keyword
                  density and better formatting get ranked higher. Superpower Resume shows your
                  match score before you submit so you can optimize first.
                </p>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Internal Links Section */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">
              Related Resources
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link
                href="/blog/beat-ats-systems"
                className="bg-white rounded-xl p-6 shadow border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-gray-900 mb-2">How to Beat ATS Systems in 2026</h3>
                <p className="text-sm text-gray-500">
                  A complete guide to getting your resume past applicant tracking systems.
                </p>
              </Link>
              <Link
                href="/blog/how-to-write-a-resume"
                className="bg-white rounded-xl p-6 shadow border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-gray-900 mb-2">How to Write a Resume</h3>
                <p className="text-sm text-gray-500">
                  Step-by-step guide to writing a resume that gets interviews.
                </p>
              </Link>
              <Link
                href="/blog/resume-keywords-by-industry"
                className="bg-white rounded-xl p-6 shadow border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-gray-900 mb-2">Resume Keywords by Industry</h3>
                <p className="text-sm text-gray-500">
                  The exact keywords hiring managers search for in every major industry.
                </p>
              </Link>
              <Link
                href="/tools/ats-resume-checker"
                className="bg-white rounded-xl p-6 shadow border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-gray-900 mb-2">Free ATS Resume Checker</h3>
                <p className="text-sm text-gray-500">
                  Score your resume against any job description instantly.
                </p>
              </Link>
              <Link
                href="/blog/cover-letter-templates"
                className="bg-white rounded-xl p-6 shadow border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-gray-900 mb-2">Cover Letter Templates</h3>
                <p className="text-sm text-gray-500">
                  Professional cover letter templates for every industry.
                </p>
              </Link>
              <Link
                href="/glossary"
                className="bg-white rounded-xl p-6 shadow border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <h3 className="font-bold text-gray-900 mb-2">ATS & Resume Glossary</h3>
                <p className="text-sm text-gray-500">
                  Learn the terminology behind applicant tracking systems and resume optimization.
                </p>
              </Link>
            </div>
          </section>
        </ScrollReveal>

        {/* FAQ */}
        <ScrollReveal>
          <section className="mb-20">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqItems.map(({ q, a }) => (
                <details
                  key={q}
                  className="group bg-white rounded-xl border border-gray-200 shadow-sm"
                >
                  <summary className="cursor-pointer px-6 py-5 text-lg font-semibold text-gray-900 flex items-center justify-between list-none">
                    {q}
                    <span className="ml-4 text-gray-400 group-open:rotate-45 transition-transform duration-200 text-2xl leading-none">
                      +
                    </span>
                  </summary>
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed">{a}</div>
                </details>
              ))}
            </div>
          </section>
        </ScrollReveal>

        {/* Final CTA */}
        <ScrollReveal>
          <section className="flex justify-center">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl shadow-2xl px-8 py-12 max-w-xl w-full flex flex-col items-center">
              <h2 className="text-3xl font-extrabold mb-4 text-center text-white tracking-tight">
                Stop getting filtered out by ATS
              </h2>
              <p className="text-lg text-blue-100 mb-8 text-center">
                Build an ATS-optimized resume, practice your interview, and optimize your LinkedIn
                — all in one platform. Free to start.
              </p>
              <a
                href="https://app.superpowerresume.com"
                className="inline-block bg-white text-blue-700 font-bold py-3 px-8 rounded-xl shadow text-lg hover:bg-blue-50 hover:scale-105 hover:shadow-xl transition-all duration-200"
              >
                Get Started Free
              </a>
            </div>
          </section>
        </ScrollReveal>
      </main>
    </div>
  );
}
